/**
 * ServiceCompanySetup.tsx
 *
 * Design Philosophy: Editorial Minimalism with Architectural Precision
 * - Clean typography hierarchy with generous whitespace
 * - Muted earth tones (warm beige, soft grey, deep burgundy accents)
 * - Structured grid layouts with asymmetric photo placements
 * - Subtle shadows and borders for depth without heaviness
 */

import { Link } from "wouter";
import { Helmet } from "react-helmet-async";
import { ArrowRight, Building2, FileText, Receipt, Landmark, Users, CheckCircle2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";

const HERO_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/cs-hero-office_4a9dd604.jpg";
const DOCS_IMG = "https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/cs-documents-4aoWRzMXoAw7nseXWY9y65.webp";

export default function ServiceCompanySetup() {
  return (
    <>
    <Helmet>
      <title>Company Setup in Romania — Register Your SRL in Brașov</title>
      <meta name="description" content="We handle the full SRL registration process in Romania for foreigners — from paperwork to bank account opening. Fixed fee, no surprises. Based in Brașov." />
      <meta property="og:title" content="Company Setup in Romania — Register Your SRL in Brașov" />
      <meta property="og:description" content="We handle the full SRL registration process in Romania for foreigners — from paperwork to bank account opening. Fixed fee, no surprises. Based in Brașov." />
      <meta property="og:image" content="https://d2xsxph8kpxj0f.cloudfront.net/310419663030265086/PFvGN8AsGBMgfaYEU36oKA/home-hero-brasov-v2_fd2b2f8b.webp" />
      <meta property="og:locale" content="en_US" />
      <meta property="og:type" content="website" />
      <meta property="og:url" content="https://brasovgateway.com/services/company-setup" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Company Setup in Romania — Register Your SRL in Brașov" />
      <link rel="canonical" href="https://brasovgateway.com/services/company-setup" />
      <script type="application/ld+json">{`
        {
          "@context": "https://schema.org",
          "@type": "Service",
          "@id": "https://brasovgateway.com/services/company-setup",
          "name": "Company Setup in Romania",
          "url": "https://brasovgateway.com/services/company-setup",
          "description": "Full company registration service in Romania for foreigners — SRL setup, tax registration, bank account opening, and ongoing compliance support.",
          "provider": {
            "@type": "Organization",
            "name": "Brașov Gateway",
            "url": "https://brasovgateway.com"
          },
          "areaServed": {
            "@type": "Country",
            "name": "Romania"
          },
          "serviceType": "Company Registration"
        }
      `}</script>
    </Helmet>
    <div className="min-h-screen bg-background">
      <SiteHeader />

      {/* Hero Section */}
      <section className="relative h-[70vh] min-h-[500px] flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/65 via-black/45 to-black/70" />
        </div>

        <div className="container relative z-10 text-center text-white px-4">
          <p className="text-sm uppercase tracking-[0.2em] mb-4 text-white/80">Services</p>
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif mb-6">
            Company Setup in Romania
          </h1>
          <p className="text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed text-white/90">
            Open a Romanian SRL, get your fiscal registration number, and stay on top of your tax obligations — without the paperwork headache.
          </p>
        </div>
      </section>

      {/* Why Romania Section */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container">
          <div className="grid md:grid-cols-2 gap-16 items-center max-w-5xl mx-auto">
            <div>
              <h2 className="text-3xl md:text-4xl font-serif mb-8 text-foreground">
                Why set up a company in Romania?
              </h2>
              <div className="space-y-5 text-muted-foreground leading-relaxed">
                <p>
                  Romania offers one of the most competitive tax environments in the EU. A standard SRL pays <strong>16% corporate income tax</strong> on profit. Newly incorporated companies with annual revenues under <strong>€100,000</strong> may qualify for the <strong>micro-enterprise regime</strong>, taxed at just <strong>1% on turnover</strong> — one of the lowest effective rates in the EU.
                </p>
                <p>
                  As an EU member state, a Romanian company gives you full access to the single market, EU banking, and cross-border trade without the complexity of offshore structures.
                </p>
                <p>
                  Brașov, specifically, has a growing ecosystem of tech companies, freelancers, and international entrepreneurs who chose Romania not just for the tax rate, but for the quality of life.
                </p>
              </div>
            </div>

            {/* Key facts */}
            <div className="grid grid-cols-2 gap-4">
              {[
                { value: "1%", label: "Micro-enterprise tax (under €100K revenue)" },
                { value: "16%", label: "Standard corporate tax rate (SRL)" },
                { value: "EU", label: "Full single market access" },
                { value: "ONRC", label: "Trade Register authority" },
              ].map((fact, idx) => (
                <div key={idx} className="bg-muted/40 border border-border p-6 text-center">
                  <div className="text-3xl font-serif font-bold mb-2" style={{ color: "oklch(0.38 0.12 15)" }}>
                    {fact.value}
                  </div>
                  <div className="text-sm text-muted-foreground leading-tight">{fact.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20 md:py-28 bg-muted/30">
        <div className="container">
          <h2 className="text-3xl md:text-4xl font-serif mb-12 text-center">
            What I help you with
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                icon: Building2,
                title: "SRL Incorporation",
                description: "I handle the full incorporation process at the Romanian Trade Register (ONRC) — articles of association, shareholder documentation, registered address, and submission."
              },
              {
                icon: FileText,
                title: "Fiscal Registration (ANAF)",
                description: "After incorporation, I obtain your company's CUI (Cod Unic de Înregistrare) from ANAF — the unique registration code assigned to every SRL, required for invoicing, banking, and all business activity in Romania. Note: NIF (Număr de Identificare Fiscală) is a separate identifier used for individuals, not companies."
              },
              {
                icon: Receipt,
                title: "Tax Compliance Monitoring",
                description: "I track your quarterly and annual tax deadlines, remind you of upcoming obligations, and coordinate with your accountant to ensure nothing is missed."
              },
              {
                icon: Landmark,
                title: "Bank Account Assistance",
                description: "I assist with opening a Romanian business bank account — identifying the right bank for your needs and guiding you through the documentation process."
              },
              {
                icon: Users,
                title: "Accountant Referrals",
                description: "I connect you with trusted local accountants who work with international clients and can handle your monthly bookkeeping and declarations."
              },
              {
                icon: CheckCircle2,
                title: "Ongoing Advisory",
                description: "As your business evolves, I'm available for questions about Romanian regulations, structural changes, or expanding your operations in Brașov."
              }
            ].map((service, idx) => (
              <div key={idx} className="bg-card p-8 rounded-sm border border-border hover:shadow-lg transition-shadow">
                <service.icon className="w-10 h-10 mb-4 text-primary" />
                <h3 className="text-xl font-semibold mb-3 text-card-foreground">{service.title}</h3>
                <p className="text-muted-foreground leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Documents Image */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container">
          <div className="max-w-5xl mx-auto">
            <img
              src={DOCS_IMG}
              alt="Romanian company registration documents"
              className="w-full h-auto rounded-sm shadow-xl mb-6"
            />
            <p className="text-center text-muted-foreground italic text-sm">
              Romanian incorporation documents — Certificate de Înregistrare, fiscal registration, and articles of association.
            </p>
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-20 md:py-28 bg-muted/30">
        <div className="container">
          <h2 className="text-3xl md:text-4xl font-serif mb-16 text-center">
            The process, step by step
          </h2>

          <div className="max-w-4xl mx-auto space-y-12">
            {[
              {
                step: "01",
                title: "Initial Consultation",
                description: "We discuss your business activity, ownership structure, and any specific requirements. I explain the options and what to expect at each stage."
              },
              {
                step: "02",
                title: "Document Preparation",
                description: "I prepare the articles of association, shareholder declarations, and all required forms. You provide identification documents and sign where needed."
              },
              {
                step: "03",
                title: "ONRC Submission",
                description: "I submit your incorporation file to the Romanian Trade Register (ONRC) and follow up on the status. Standard processing takes up to 14 working days."
              },
              {
                step: "04",
                title: "Fiscal Registration (ANAF)",
                description: "Once the company is registered, I obtain your CUI (Cod Unic de Înregistrare) from ANAF — your company's unique registration code, required for all fiscal and commercial activity in Romania."
              },
              {
                step: "05",
                title: "Bank Account Opening",
                description: "I assist with selecting a bank and preparing the documentation for your business account. Most banks require an in-person visit, which I accompany you to."
              },
              {
                step: "06",
                title: "Ongoing Compliance",
                description: "I monitor your tax calendar, remind you of quarterly and annual deadlines, and coordinate with your accountant to keep everything in order."
              }
            ].map((item, idx) => (
              <div key={idx} className="flex gap-8 items-start">
                <div className="flex-shrink-0 w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="text-2xl font-serif text-primary">{item.step}</span>
                </div>
                <div className="flex-1 pt-2">
                  <h3 className="text-2xl font-semibold mb-3 text-foreground">{item.title}</h3>
                  <p className="text-muted-foreground leading-relaxed">{item.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-background">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-serif mb-6">
              Ready to open your Romanian company?
            </h2>
            <p className="text-xl text-muted-foreground mb-8 leading-relaxed">
              Whether you're a freelancer, a startup founder, or an investor, I'll guide you through every step — from choosing the right structure to staying compliant.
            </p>
            <Link href="/contact">
              <Button size="lg" className="group">
                Schedule a Consultation
                <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
    </>
  );
}
